import type { SignalBar } from './signalBars'

/**
 * Rows for the per-device "Base stations" grid: one row per station, one cell
 * per day, aligned with the Daily signal strip above it.
 *
 * A cell's value is the station's SHARE of that day's messages, not a raw
 * count, so a day the device sent half as much does not read as the station
 * fading. It is shown as a single-hue ramp (density), so the station's identity
 * comes from the row label and never has to be told apart by colour.
 *
 * Rows are the busiest stations over the whole window, at most MAX_ROWS; any
 * beyond fold into one "Other" row rather than growing the card without bound.
 */

export const MAX_STATION_ROWS = 4
export const OTHER_STATION = 'Other'

export interface StationCell {
  day: string
  /** Messages carried by this station that day. */
  count: number
  /** count / that day's messages with a station; 0 when nothing was heard. */
  share: number
  /** That day's messages with a station, for the tooltip. */
  dayTotal: number
  rssiMedian: number | null
}

export interface StationRow {
  station: string
  total: number
  cells: StationCell[]
}

export function stationGrid(bars: SignalBar[]): StationRow[] {
  const totals = new Map<string, number>()
  for (const bar of bars) {
    for (const s of bar.row?.stations ?? []) totals.set(s.id, (totals.get(s.id) ?? 0) + s.count)
  }

  const ranked = [...totals].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([id]) => id)
  const fold = ranked.length > MAX_STATION_ROWS
  const named = fold ? ranked.slice(0, MAX_STATION_ROWS - 1) : ranked
  const keys = fold ? [...named, OTHER_STATION] : named
  const keyOf = (id: string) => (named.includes(id) ? id : OTHER_STATION)

  return keys.map((station) => {
    const cells = bars.map((bar): StationCell => {
      const stations = bar.row?.stations ?? []
      const dayTotal = stations.reduce((sum, s) => sum + s.count, 0)
      const mine = stations.filter((s) => keyOf(s.id) === station)
      const count = mine.reduce((sum, s) => sum + s.count, 0)
      return {
        day: bar.day,
        count,
        share: dayTotal > 0 ? count / dayTotal : 0,
        dayTotal,
        // A folded row mixes stations, so one median would describe none of them.
        rssiMedian: mine.length === 1 ? mine[0].rssiMedian : null,
      }
    })
    return { station, total: cells.reduce((sum, c) => sum + c.count, 0), cells }
  })
}

/**
 * Share to one of five density steps. 0 is reserved for "not heard that day",
 * so any non-zero share is at least step 1 and a single message never vanishes.
 */
export function shareStep(share: number): 0 | 1 | 2 | 3 | 4 {
  if (share <= 0) return 0
  if (share < 0.25) return 1
  if (share < 0.5) return 2
  if (share < 0.75) return 3
  return 4
}
